import { useTranslation } from 'react-i18next';
import { ScrollShadow } from '@heroui/react/scroll-shadow';
import { LifeBuoy, Settings } from 'lucide-react';
import { cn } from '@/utils/cn';
import { BranchSwitcher } from '../BranchSwitcher';
import { ClinicSwitcher } from '../ClinicSwitcher';
import { SidebarNav, type SidebarNavProps } from './SidebarNav';
import { NavLinkRow } from './NavItem';
import { useActiveNav, useVisibleSettings } from './use-nav-state';

/**
 * The sidebar shell: brand slot, branch picker, scrolling nav, footer.
 *
 * Deliberately *presentational* — it owns no state. Collapse and group
 * state are passed in from AppShell, which is what lets the desktop
 * rail and the mobile drawer share one nav without sharing a toggle.
 *
 * ScrollShadow (HeroUI) replaces the old bare `overflow-y-auto`: with
 * 60+ destinations the cut-off at the fold was invisible, so the rail
 * looked like it ended at whatever row happened to be last.
 */

export interface SidebarProps extends SidebarNavProps {
  /** Rendered inside a drawer — hides the collapse affordance. */
  inDrawer?: boolean;
}

export function Sidebar({
  collapsed,
  inDrawer = false,
  onNavigate,
  ...navProps
}: SidebarProps) {
  const { t } = useTranslation();
  const { isLeafActive, inSettings } = useActiveNav();
  const hasSettings = useVisibleSettings(navProps.permissions).length > 0;
  const isCollapsed = collapsed && !inDrawer;

  return (
    <div className="flex h-full min-h-0 flex-col bg-surface-1">
      <ClinicSwitcher collapsed={isCollapsed} onCloseMobileMenu={onNavigate} />

      <div className="border-b border-border-subtle">
        <BranchSwitcher collapsed={isCollapsed} />
      </div>

      <ScrollShadow className="min-h-0 flex-1 scroll-thin" size={20}>
        <SidebarNav
          {...navProps}
          collapsed={isCollapsed}
          onNavigate={onNavigate}
        />
      </ScrollShadow>

      <div className="space-y-0.5 border-t border-border-subtle p-2">
        {/* Pinned, not at the end of the scrolling list: with every group
            open it sat a long scroll below the fold. Inside settings the
            list above becomes the settings nav, whose "All settings" row is
            this same link, so it steps aside rather than repeat it. */}
        {hasSettings && !inSettings && (
          <NavLinkRow
            to="/dashboard/settings"
            icon={Settings}
            label={t('nav.settings')}
            isActive={false}
            collapsed={isCollapsed}
            onNavigate={onNavigate}
          />
        )}
        {/* One entry point. The kind of report is chosen in the form —
            previously "Feature Request" and "Quick Feedback" were two rows
            that both produced a record of the same type. */}
        <NavLinkRow
          to="/dashboard/feedback"
          icon={LifeBuoy}
          label={t('nav.helpAndFeedback')}
          isActive={isLeafActive({
            kind: 'link',
            id: 'feedback',
            labelKey: '',
            icon: LifeBuoy,
            href: '/dashboard/feedback',
          })}
          collapsed={isCollapsed}
          onNavigate={onNavigate}
        />

        <a
          href="https://dentacares.com"
          target="_blank"
          rel="noopener noreferrer"
          className={cn(
            'group mt-1 flex items-center border-t border-border-subtle pt-2',
            'no-underline hover:no-underline',
            isCollapsed ? 'justify-center' : 'justify-center gap-1.5 px-3',
          )}
        >
          <img
            src="/logo.svg"
            alt=""
            aria-hidden
            className="size-3.5 shrink-0 opacity-50 transition-opacity group-hover:opacity-80"
          />
          {!isCollapsed && (
            <span className="text-micro leading-none text-secondary-400 transition-colors group-hover:text-secondary-600">
              {t('shell.poweredBy')}{' '}
              <span className="font-semibold text-secondary-500 group-hover:text-brand-text">
                {t('screens.sidebar.dentacares')}
              </span>
            </span>
          )}
        </a>
      </div>
    </div>
  );
}
